import React, { useCallback, useEffect, useState } from 'react';
import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { PencilIcon, SearchIcon } from 'lucide-react';
import apiClient from '../Services/ApiClient';
import { useSnackbar } from '../Context/SnackbarProvider';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { textFieldSx } from '../Components/Form';
import { COLORS } from '../Styles/Colors';
import {
  AddButton,
  CancelButton,
  CloseButton,
  DeleteConfirmButton,
  EditButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  PageContainer,
  TealSwitch,
} from '../Styles/AppStyles';
import { AccountListFilter, CustomerAccountSummary } from '../Types/customerAccounts';
import CustomerAccountForm, { NewCustomerAccount } from './CustomerAccounts/CustomerAccountForm';
import CustomerAccountDetail from './CustomerAccounts/CustomerAccountDetail';
import { KIND_SHORT, formatDate } from './CustomerAccounts/labels';

const toggleSx = {
  '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontSize: '0.8125rem' },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: COLORS.teal,
    color: COLORS.white,
  },
};

/**
 * Admin > Customer Accounts (AIMS Admin only). Who can sign in to the mobile app as a
 * customer, which P21 customers they see, and who changed what. abatix.com customers and
 * AIMS users get their accounts automatically; SMS customers are added here.
 */
const CustomerAccountsPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [filter, setFilter] = useState<AccountListFilter>('customers');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState<CustomerAccountSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [openAccountId, setOpenAccountId] = useState<number | null>(null);
  const [toDeactivate, setToDeactivate] = useState<CustomerAccountSummary | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        kind: filter,
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (search) params.set('search', search);
      const response = await apiClient(`/api/customer-accounts?${params}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Could not load accounts');
      setRows(body.accounts);
      setTotal(body.total);
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  }, [filter, search, page, pageSize, showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const setActive = async (account: CustomerAccountSummary, isActive: boolean) => {
    try {
      const response = await apiClient(`/api/customer-accounts/${account.accountId}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      });
      const body = await response.json();
      if (!response.ok) {
        showSnackbar(body.error || 'The change was not saved', 'error', 'Not saved');
        return;
      }
      showSnackbar(isActive ? 'Account activated' : 'Account deactivated', 'success', 'Saved');
      load();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    }
  };

  const handleCreate = async (input: NewCustomerAccount) => {
    try {
      const response = await apiClient('/api/customer-accounts', { method: 'POST', body: JSON.stringify(input) });
      const body = await response.json();
      if (!response.ok) {
        showSnackbar(body.error || 'The customer was not added', 'error', 'Not added');
        return;
      }
      showSnackbar('Customer added. They can now sign in with an SMS code.', 'success', 'Added');
      setShowAdd(false);
      setOpenAccountId(body.accountId);
      load();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    }
  };

  const who = (row: CustomerAccountSummary) =>
    row.email ?? row.workerName ?? row.phoneE164 ?? `Account #${row.accountId}`;

  const columns: ColumnDef<CustomerAccountSummary>[] = [
    {
      field: 'actions' as keyof CustomerAccountSummary,
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <EditButton onClick={() => setOpenAccountId(params.row.accountId)} aria-label={`Open ${who(params.row)}`}>
            <PencilIcon />
          </EditButton>
        </div>
      ),
    },
    { field: 'email', headerName: 'Account', width: 260, valueGetter: (_, row) => who(row as CustomerAccountSummary) },
    { field: 'kind', headerName: 'Type', width: 120, valueGetter: (value) => KIND_SHORT[value as keyof typeof KIND_SHORT] ?? value },
    { field: 'websiteRole', headerName: 'Website Role', width: 130 },
    { field: 'phoneE164', headerName: 'Mobile', width: 140 },
    {
      field: 'customerIds',
      headerName: 'P21 Customers',
      width: 220,
      sortable: false,
      valueGetter: (value) => (Array.isArray(value) ? value.join(', ') : ''),
    },
    { field: 'createdAt', headerName: 'Created', width: 130, valueGetter: (value) => formatDate(value as string) },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <TealSwitch
          checked={params.row.isActive}
          onChange={() => (params.row.isActive ? setToDeactivate(params.row) : setActive(params.row, true))}
          inputProps={{ 'aria-label': `Can ${who(params.row)} sign in` }}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      <Header
        title="Customer Accounts"
        subtitle="Who can sign in to the mobile app as a customer, and which P21 customers they see"
        count={total}
        hideCount
      >
        <AddButton onClick={() => setShowAdd(true)} title="Add an SMS customer">+</AddButton>
      </Header>

      <DataTable
        data={rows}
        columns={columns}
        pageKey="customer-accounts"
        title="Customer Accounts"
        loading={loading}
        idField="accountId"
        hideSearchBar
        hideColumnFilters
        serverSidePagination
        page={page}
        rowCount={total}
        initialPageSize={pageSize}
        pageSizeOptions={[20, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          if (size === pageSize) return;
          setPageSize(size);
          setPage(0);
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          sx={toggleSx}
          onChange={(_, value) => {
            if (!value) return;
            setFilter(value);
            setPage(0);
          }}
        >
          <ToggleButton value="customers">Customers</ToggleButton>
          <ToggleButton value="workers">AIMS Users</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, email, mobile or P21 customer"
          sx={{ ...textFieldSx, width: { xs: '100%', md: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={16} />
              </InputAdornment>
            ),
          }}
        />
      </DataTable>

      {showAdd && (
        <ModalOverlay>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowAdd(false)}>×</CloseButton>
            <ModalHeader>Add SMS Customer</ModalHeader>
            <CustomerAccountForm onSubmit={handleCreate} onCancel={() => setShowAdd(false)} />
          </ModalContent>
        </ModalOverlay>
      )}

      {openAccountId != null && (
        <ModalOverlay>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setOpenAccountId(null)}>×</CloseButton>
            <ModalHeader>Customer Account</ModalHeader>
            <CustomerAccountDetail accountId={openAccountId} onChanged={load} onClose={() => setOpenAccountId(null)} />
          </ModalContent>
        </ModalOverlay>
      )}

      {toDeactivate && (
        <ModalOverlay onClick={() => setToDeactivate(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setToDeactivate(null)}>×</CloseButton>
            <ModalHeader>Confirm Deactivation</ModalHeader>
            <p>
              Deactivate <strong>{who(toDeactivate)}</strong>? They can't use the AIMS app until it's turned back on.
              This applies right away, even if they are signed in.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 5, alignItems: 'center' }}>
              <CancelButton onClick={() => setToDeactivate(null)}>Cancel</CancelButton>
              <DeleteConfirmButton
                onClick={async () => {
                  await setActive(toDeactivate, false);
                  setToDeactivate(null);
                }}
              >
                Deactivate
              </DeleteConfirmButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default CustomerAccountsPage;
